import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../utils/axiosInstance';
import {
  FaSync, FaSearch, FaSms, FaFilter, FaCheckCircle,
  FaTimesCircle, FaClock, FaChevronLeft, FaChevronRight, FaTimes,
  FaInfoCircle, FaCalendarAlt, FaArrowRight, FaEye, FaCopy, FaCheck,
  FaExclamationTriangle, FaStopwatch
} from 'react-icons/fa';
import dayjs from 'dayjs';

const toTR = t => { if (!t) return null; const d = dayjs(t); return d.isValid() ? d.add(3, 'hour') : null; };
const trFull = t => { const d = toTR(t); return d ? d.format('DD.MM.YYYY HH:mm:ss') : '—'; };
const trShort = t => { const d = toTR(t); return d ? d.format('DD.MM HH:mm') : '—'; };
const getSender = s => s.senderID || s.sender_id || s.sender || s.originator || '—';
const getPart = s => { const p = Object.values(s.parts || {}); return p[0] || {}; };
const extractCode = txt => { if (!txt) return null; const m = txt.match(/(\d{4,6})\s*$/); return m ? m[1] : null; };

const ST = {
  DELIVERD: { l: 'Teslim Edildi', c: '#34d399' }, SENT: { l: 'Gönderildi', c: '#60a5fa' },
  ACCEPTED: { l: 'Kabul Edildi', c: '#22d3ee' }, EXPIRED: { l: 'Süresi Doldu', c: '#fbbf24' },
  REJECTED: { l: 'Reddedildi', c: '#f87171' }, UNDELIV: { l: 'Teslim Edilemedi', c: '#fb923c' },
  UNKNOWN: { l: 'Bilinmiyor', c: '#94a3b8' }, SCHEDULED: { l: 'Planlandı', c: '#c084fc' },
};
const gs = s => ST[s] || { l: s || '—', c: '#94a3b8' };

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=IBM+Plex+Mono:wght@400;500;600&display=swap');
*{box-sizing:border-box}
@keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
@keyframes spin{to{transform:rotate(360deg)}}
@keyframes flashGreen{0%{box-shadow:0 0 0 0 rgba(52,211,153,.4)}100%{box-shadow:0 0 0 6px rgba(52,211,153,0)}}
@keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}
.si{animation:fadeUp .3s ease both}
.sr{transition:background .1s}.sr:hover{background:rgba(255,255,255,.02)!important}
.ss::-webkit-scrollbar{width:3px}.ss::-webkit-scrollbar-thumb{background:rgba(255,255,255,.06);border-radius:8px}
.sf{animation:flashGreen .4s ease}
.sp{animation:pulse 1s ease infinite}
`;

const card = { background: 'linear-gradient(135deg,rgba(17,19,25,.95),rgba(13,15,19,.98))', border: '1px solid rgba(255,255,255,.05)', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,.2),inset 0 1px 0 rgba(255,255,255,.03)' };
const inp = { padding: '9px 13px', borderRadius: 10, border: '1px solid rgba(255,255,255,.08)', background: 'rgba(255,255,255,.025)', color: '#e2e8f0', fontSize: '.82rem', outline: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif" };

/* ═══ CODE BADGE ═══ */
const CodeBadge = ({ code }) => {
  const [ok, setOk] = useState(false);
  if (!code) return <span style={{ color: '#1e293b' }}>—</span>;
  return (
    <button className={ok ? 'sf' : ''} onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(code); setOk(true); setTimeout(() => setOk(false), 1200); }}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 7,
        background: ok ? 'rgba(52,211,153,.1)' : 'rgba(242,113,39,.06)', border: `1px solid ${ok ? 'rgba(52,211,153,.2)' : 'rgba(242,113,39,.12)'}`,
        color: ok ? '#34d399' : '#fbbf24', fontSize: '.78rem', fontWeight: 600, cursor: 'pointer',
        fontFamily: "'IBM Plex Mono',monospace", transition: 'all .15s', whiteSpace: 'nowrap' }}>
      {ok ? <><FaCheck size={8} /> OK</> : <><FaCopy size={8} /> {code}</>}
    </button>
  );
};

/* ═══ COUNTDOWN TIMER ═══ */
const Countdown = ({ smsTime }) => {
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    if (!smsTime) return;
    const expiry = toTR(smsTime);
    if (!expiry) return;
    const expiryMs = expiry.add(2, 'minute').valueOf();

    const tick = () => {
      const now = Date.now();
      const diff = expiryMs - now;
      setRemaining(diff > 0 ? diff : 0);
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [smsTime]);

  if (remaining === null) return null;

  if (remaining <= 0) {
    return (
      <span style={{ fontSize: '.68rem', color: '#f87171', fontWeight: 600, whiteSpace: 'nowrap' }}>
        Süresi Doldu
      </span>
    );
  }

  const min = Math.floor(remaining / 60000);
  const sec = Math.floor((remaining % 60000) / 1000);
  const isLow = remaining < 30000;

  return (
    <span className={isLow ? 'sp' : ''} style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: '.72rem', fontWeight: 600, fontFamily: "'IBM Plex Mono',monospace",
      color: isLow ? '#f87171' : '#34d399', whiteSpace: 'nowrap',
    }}>
      <FaStopwatch size={9} />
      {min}:{sec.toString().padStart(2, '0')}
    </span>
  );
};

/* ═══ MODAL ═══ */
const Overlay = ({ children, onClose, w = 480 }) => (
  <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,.5)', backdropFilter: 'blur(12px)' }} onClick={onClose}>
    <div onClick={e => e.stopPropagation()} style={{ width: w, background: '#111318', borderRadius: 20, border: '1px solid rgba(255,255,255,.07)', padding: 28, boxShadow: '0 32px 80px rgba(0,0,0,.5)' }}>{children}</div>
  </div>
);

const DetailModal = ({ sms, onClose }) => {
  const part = getPart(sms); const st = gs(part.state); const sender = getSender(sms); const code = extractCode(sms.text_sms);
  return (
    <Overlay onClose={onClose} w={520}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 48, height: 48, borderRadius: 13, background: `${st.c}12`, border: `1px solid ${st.c}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FaSms size={18} color={st.c} /></div>
          <div>
            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#f1f5f9', fontFamily: "'IBM Plex Mono',monospace" }}>{sms.phone}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
              <span style={{ fontSize: '.78rem', fontWeight: 600, color: st.c }}>{st.l}</span>
              <span style={{ color: '#334155' }}>·</span>
              <span style={{ fontSize: '.72rem', color: '#f27127', fontWeight: 600 }}>{sender}</span>
            </div>
          </div>
        </div>
        <button onClick={onClose} style={{ background: 'rgba(255,255,255,.04)', border: 'none', color: '#94a3b8', cursor: 'pointer', width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FaTimes size={14} /></button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
        {[{ l: 'Gönderim Tarihi', v: trFull(sms.time), c: '#f1f5f9', m: true }, { l: 'Operatör', v: sms.destination || `MCC:${sms.MCC} MNC:${sms.MNC}`, c: '#22d3ee' },
        { l: 'Tip / Encoding', v: `${(sms.type_sms || 'sms').toUpperCase()} · ${sms.encoding || '?'}`, c: '#c084fc' }, { l: 'Parça / Fiyat', v: `${sms.num_parts || 1} parça · ${part.price ? '$' + part.price : '—'}`, c: '#fbbf24' },
        { l: 'Geçerlilik', v: trFull(sms.validity_period), c: '#94a3b8', m: true }, { l: 'Durum Güncelleme', v: trFull(part.time_change_state), c: '#94a3b8', m: true }].map((r, i) => (
          <div key={i} style={{ padding: '10px 14px', background: 'rgba(255,255,255,.018)', borderRadius: 10, border: '1px solid rgba(255,255,255,.03)' }}>
            <div style={{ fontSize: '.66rem', color: '#475569', fontWeight: 600, marginBottom: 3 }}>{r.l}</div>
            <div style={{ fontSize: '.86rem', fontWeight: 600, color: r.c, fontFamily: r.m ? "'IBM Plex Mono',monospace" : 'inherit' }}>{r.v}</div>
          </div>
        ))}
      </div>
      <div style={{ padding: 14, background: 'rgba(255,255,255,.018)', borderRadius: 12, border: '1px solid rgba(255,255,255,.03)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: '.72rem', color: '#64748b', fontWeight: 600 }}>Mesaj</span>
          <div style={{ display: 'flex', gap: 6 }}>{code && <CodeBadge code={code} />}
            <button onClick={() => navigator.clipboard.writeText(sms.text_sms || '')} style={{ background: 'rgba(255,255,255,.04)', border: 'none', color: '#64748b', cursor: 'pointer', padding: '3px 8px', borderRadius: 6, fontSize: '.65rem', display: 'flex', alignItems: 'center', gap: 3 }}><FaCopy size={8} /> Kopyala</button>
          </div>
        </div>
        <div style={{ fontSize: '.95rem', color: '#f1f5f9', whiteSpace: 'pre-wrap', lineHeight: 1.7, fontFamily: "'IBM Plex Mono',monospace" }}>{sms.text_sms}</div>
      </div>
    </Overlay>
  );
};

/* ═══ MAIN ═══ */
const SmsManagement = () => {
  const [all, setAll] = useState([]); const [ld, setLd] = useState(false); const [search, setSearch] = useState(''); const [stF, setStF] = useState('');
  const [dr, setDr] = useState({ s: dayjs().subtract(2, 'day').format('YYYY-MM-DD'), e: dayjs().format('YYYY-MM-DD') });
  const [pg, setPg] = useState(1); const [det, setDet] = useState(null);
  const PP = 20;

  const load = useCallback(async () => { setLd(true); try { const b = { time_period: `${dr.s} 00:00:00 - ${dr.e} 23:59:59` }; if (search.trim()) b.phone = [search.replace(/\D/g, '')]; const { data } = await axiosInstance.post('/sms/list', b); setAll(data?.data || []); setPg(1); } catch (e) { console.error(e); setAll([]); } setLd(false); }, [dr, search]);
  useEffect(() => { load(); }, [load]);

  const filt = stF === '_PENDING' ? all.filter(s => ['SENT', 'ACCEPTED', 'SCHEDULED'].includes(getPart(s).state)) : stF ? all.filter(s => getPart(s).state === stF) : all;
  const tp = Math.ceil(filt.length / PP) || 1;
  const rows = filt.slice((pg - 1) * PP, pg * PP);
  const del = all.filter(s => getPart(s).state === 'DELIVERD').length;
  const fail = all.filter(s => ['REJECTED', 'UNDELIV', 'EXPIRED'].includes(getPart(s).state)).length;
  const pend = all.filter(s => ['SENT', 'ACCEPTED', 'SCHEDULED'].includes(getPart(s).state)).length;

  return (
    <div style={{ padding: '26px 30px', minHeight: '100vh', background: '#07080b', fontFamily: "'Plus Jakarta Sans',sans-serif", color: '#e2e8f0' }}>
      <style>{CSS}</style>
      {det && <DetailModal sms={det} onClose={() => setDet(null)} />}

      {/* HEADER */}
      <div className="si" style={{ ...card, padding: '20px 26px', marginBottom: 22, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 46, height: 46, borderRadius: 13, background: 'linear-gradient(135deg,rgba(242,113,39,.12),rgba(242,113,39,.04))', border: '1px solid rgba(242,113,39,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FaSms size={19} color="#f27127" /></div>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: '#f1f5f9' }}>SMS Merkezi</h1>
            <p style={{ margin: '2px 0 0', fontSize: '.7rem', color: '#475569' }}>Sempico Solutions</p>
          </div>
        </div>
        <button onClick={load} title="Yenile" style={{ width: 42, height: 42, borderRadius: 11, border: '1px solid rgba(255,255,255,.06)', background: 'rgba(255,255,255,.02)', color: '#94a3b8', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .15s' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(242,113,39,.2)'; e.currentTarget.style.color = '#f27127'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,.06)'; e.currentTarget.style.color = '#94a3b8'; }}>
          <FaSync size={13} style={ld ? { animation: 'spin 1s linear infinite' } : {}} />
        </button>
      </div>

      {/* STATS */}
      <div className="si" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 20 }}>
        {[{ l: 'Toplam', v: all.length, c: '#f1f5f9' }, { l: 'Teslim Edildi', v: del, c: '#34d399' }, { l: 'Beklemede', v: pend, c: '#60a5fa' }, { l: 'Başarısız', v: fail, c: '#f87171' }].map((s, i) => (
          <div key={i} style={{ ...card, padding: '15px 18px' }}>
            <p style={{ fontSize: '.66rem', color: '#475569', fontWeight: 600, margin: '0 0 5px', textTransform: 'uppercase', letterSpacing: '.04em' }}>{s.l}</p>
            <p style={{ fontSize: '1.35rem', fontWeight: 800, color: s.c, margin: 0, fontFamily: "'IBM Plex Mono',monospace" }}>{s.v}</p>
          </div>
        ))}
      </div>

      {/* FİLTRELER */}
      <div className="si" style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <FaCalendarAlt size={12} color="#64748b" />
        <input type="date" value={dr.s} onChange={e => setDr(p => ({ ...p, s: e.target.value }))} style={inp} />
        <FaArrowRight size={9} color="#334155" />
        <input type="date" value={dr.e} onChange={e => setDr(p => ({ ...p, e: e.target.value }))} style={inp} />
        <div style={{ position: 'relative' }}>
          <FaSearch size={11} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: '#475569' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Telefon ara..." style={{ ...inp, paddingLeft: 32, width: 175, fontFamily: "'IBM Plex Mono',monospace" }} />
        </div>
        <button onClick={load} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '9px 18px', borderRadius: 10, border: '1px solid rgba(242,113,39,.2)', background: 'rgba(242,113,39,.06)', color: '#f27127', fontSize: '.82rem', fontWeight: 600, cursor: 'pointer', transition: 'background .15s' }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(242,113,39,.12)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(242,113,39,.06)'}>
          <FaFilter size={10} /> Ara
        </button>
        <span style={{ marginLeft: 'auto', fontSize: '.76rem', color: '#475569' }}>{filt.length} sonuç</span>
      </div>

      {/* DURUM PİLLERİ */}
      <div className="si" style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={() => { setStF(''); setPg(1); }}
          style={{ padding: '6px 14px', borderRadius: 20, border: !stF ? '1px solid rgba(242,113,39,.3)' : '1px solid rgba(255,255,255,.06)', background: !stF ? 'rgba(242,113,39,.1)' : 'rgba(255,255,255,.02)', color: !stF ? '#f27127' : '#64748b', fontSize: '.72rem', fontWeight: 600, cursor: 'pointer', transition: 'all .12s', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
          Tümü <span style={{ marginLeft: 3, fontFamily: "'IBM Plex Mono',monospace", opacity: .7 }}>{all.length}</span>
        </button>
        {(() => { const c = all.filter(s => ['SENT', 'ACCEPTED', 'SCHEDULED'].includes(getPart(s).state)).length; return c > 0 ? (
          <button onClick={() => { setStF(stF === '_PENDING' ? '' : '_PENDING'); setPg(1); }}
            style={{ padding: '6px 14px', borderRadius: 20, border: stF === '_PENDING' ? '1px solid rgba(96,165,250,.4)' : '1px solid rgba(255,255,255,.06)', background: stF === '_PENDING' ? 'rgba(96,165,250,.15)' : 'rgba(255,255,255,.02)', color: stF === '_PENDING' ? '#60a5fa' : '#64748b', fontSize: '.72rem', fontWeight: 600, cursor: 'pointer', transition: 'all .12s', display: 'flex', alignItems: 'center', gap: 6, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#60a5fa', opacity: stF === '_PENDING' ? 1 : .4 }} />
            Beklemede <span style={{ fontFamily: "'IBM Plex Mono',monospace", opacity: .6 }}>{c}</span>
          </button>
        ) : null; })()}
        {Object.entries(ST).map(([k, v]) => {
          const count = all.filter(s => getPart(s).state === k).length;
          if (count === 0) return null;
          const active = stF === k;
          return (
            <button key={k} onClick={() => { setStF(active ? '' : k); setPg(1); }}
              style={{ padding: '6px 14px', borderRadius: 20, border: active ? `1px solid ${v.c}40` : '1px solid rgba(255,255,255,.06)', background: active ? `${v.c}15` : 'rgba(255,255,255,.02)', color: active ? v.c : '#64748b', fontSize: '.72rem', fontWeight: 600, cursor: 'pointer', transition: 'all .12s', display: 'flex', alignItems: 'center', gap: 6, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: v.c, opacity: active ? 1 : .4 }} />
              {v.l} <span style={{ fontFamily: "'IBM Plex Mono',monospace", opacity: .6 }}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* TABLO */}
      <div className="si" style={{ ...card, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '4%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '5%' }} />
          </colgroup>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(255,255,255,.05)' }}>
              {['#', 'TELEFON', 'GÖNDERİCİ', 'KOD', 'SÜRE', 'DURUM', 'TARİH', 'DETAY'].map((h, i) => (
                <th key={i} style={{ padding: '12px 10px', fontSize: '.66rem', fontWeight: 700, color: '#4a5568', textTransform: 'uppercase', letterSpacing: '.04em', textAlign: 'left', background: 'rgba(0,0,0,.06)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ld ? (
              <tr><td colSpan={8} style={{ padding: 60, textAlign: 'center' }}><FaSync size={20} color="#f27127" style={{ animation: 'spin 1s linear infinite' }} /><p style={{ marginTop: 12, color: '#64748b', fontSize: '.88rem' }}>Yükleniyor...</p></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 70, textAlign: 'center' }}><FaSms size={32} color="#1e293b" style={{ marginBottom: 10 }} /><p style={{ color: '#475569', fontSize: '.92rem', margin: 0 }}>{all.length === 0 ? 'SMS bulunamadı' : 'Filtreye uygun SMS yok'}</p></td></tr>
            ) : rows.map((sms, i) => {
              const part = getPart(sms); const st = gs(part.state); const sender = getSender(sms); const code = extractCode(sms.text_sms); const idx = (pg - 1) * PP + i + 1;
              return (
                <tr key={i} className="sr" style={{ borderBottom: '1px solid rgba(255,255,255,.015)', borderLeft: `3px solid ${st.c}20` }}>
                  <td style={{ padding: '11px 10px 11px 14px', fontSize: '.76rem', color: '#4a5568', fontFamily: "'IBM Plex Mono',monospace" }}>{idx}</td>
                  <td style={{ padding: '11px 10px', fontSize: '.86rem', fontWeight: 600, color: '#f1f5f9', fontFamily: "'IBM Plex Mono',monospace" }}>{sms.phone}</td>
                  <td style={{ padding: '11px 10px', fontSize: '.8rem', fontWeight: 600, color: '#f27127' }}>{sender}</td>
                  <td style={{ padding: '11px 8px' }}><CodeBadge code={code} /></td>
                  <td style={{ padding: '11px 8px' }}>{code ? <Countdown smsTime={sms.time} /> : <span style={{ color: '#1e293b' }}>—</span>}</td>
                  <td style={{ padding: '11px 10px' }}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '.72rem', fontWeight: 600, padding: '4px 10px', borderRadius: 16, background: `${st.c}12`, color: st.c, border: `1px solid ${st.c}18`, whiteSpace: 'nowrap' }}>{st.l}</span></td>
                  <td style={{ padding: '11px 10px', fontSize: '.8rem', color: '#94a3b8', fontFamily: "'IBM Plex Mono',monospace", whiteSpace: 'nowrap' }}>{trShort(sms.time)}</td>
                  <td style={{ padding: '11px 10px', textAlign: 'center' }}>
                    <button onClick={() => setDet(sms)} title="Detay" style={{ background: 'rgba(255,255,255,.025)', border: '1px solid rgba(255,255,255,.06)', borderRadius: 7, width: 30, height: 30, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748b', transition: 'all .12s' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(242,113,39,.12)'; e.currentTarget.style.color = '#f27127'; e.currentTarget.style.borderColor = 'rgba(242,113,39,.2)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,.025)'; e.currentTarget.style.color = '#64748b'; e.currentTarget.style.borderColor = 'rgba(255,255,255,.06)'; }}>
                      <FaEye size={11} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {filt.length > PP && (
          <div style={{ padding: '12px 18px', borderTop: '1px solid rgba(255,255,255,.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '.78rem', color: '#64748b' }}>Sayfa <b style={{ color: '#f1f5f9' }}>{pg}</b> / {tp}</span>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <button disabled={pg <= 1} onClick={() => setPg(p => p - 1)} style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,.06)', background: pg <= 1 ? 'transparent' : 'rgba(255,255,255,.025)', color: pg <= 1 ? '#1e293b' : '#94a3b8', cursor: pg <= 1 ? 'default' : 'pointer', fontSize: '.78rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}><FaChevronLeft size={9} /> Önceki</button>
              {Array.from({ length: Math.min(tp, 7) }, (_, i) => { let p; if (tp <= 7) p = i + 1; else if (pg <= 4) p = i + 1; else if (pg >= tp - 3) p = tp - 6 + i; else p = pg - 3 + i; return (<button key={p} onClick={() => setPg(p)} style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: pg === p ? 'rgba(242,113,39,.15)' : 'transparent', color: pg === p ? '#f27127' : '#64748b', fontWeight: pg === p ? 700 : 500, fontSize: '.8rem', cursor: 'pointer', fontFamily: "'IBM Plex Mono',monospace" }}>{p}</button>); })}
              <button disabled={pg >= tp} onClick={() => setPg(p => p + 1)} style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,.06)', background: pg >= tp ? 'transparent' : 'rgba(255,255,255,.025)', color: pg >= tp ? '#1e293b' : '#94a3b8', cursor: pg >= tp ? 'default' : 'pointer', fontSize: '.78rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>Sonraki <FaChevronRight size={9} /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SmsManagement;
